import React, { useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import cn from '@/utils/cn';
import CopyProgressModal from '@/components/admin/ProgramManagement/CopyProgressModal';
import SPADataTable from '../../shared/SPADataTable';
import SPAPageHeader from '../../shared/SPAPageHeader';
import AddLessonsMenu from './AddLessonsMenu';
import ProgramNavRail from './ProgramNavRail';
import NodeCrumbs from '../../shared/ProgramTree/NodeCrumbs';
import NodeTabs from '../../shared/ProgramTree/NodeTabs';
import useProgramPageTabs from '../../shared/ProgramTree/useProgramPageTabs';
import { lessonGuidesPath, programLessonsPath, useContentMount } from '../contentMount';
import UnitLessonGroup, { lessonRowId, lessonRowLabel } from './UnitLessonGroup';
import getLessonColumns from './lessonColumns';
import { LessonSavedFlashProvider, savedFlashRowClass, useLessonSavedFlash } from './lessonSavedFlash';
import useProgramLessons, { useReorderLessons } from './useProgramLessons';

const ghostHeaderClass = cn(
  'tw-px-4 tw-py-3.5 tw-rounded-t-md',
  'tw-bg-spa-bg-tertiary tw-text-spa-secondary tw-text-sm tw-font-semibold',
  'tw-border tw-border-solid tw-border-spa-border tw-border-b-0',
);

// AIDEV-NOTE: SPA recreation of the admin lessons page (/admin/programs/:id/lessons), routed on
// both mounts. Shows lessons grouped by unit with colored headers; count columns come from
// LessonSerializer extra_attributes. On the legacy mount (admin/ProgramsPage) the in-page rail
// drills into the program itself, so the SPA drill is skipped there, as on the edit page.
const ProgramLessonsPageContent = () => {
  const { programId } = useParams<{ programId: string }>();
  const pid = Number(programId);
  const mount = useContentMount();
  const inSpa = mount === 'spa';
  const {
    allLessons, units, ghostLessons, hasUnits, isLoading, isLoadingTitle, programColor, programTitle,
  } = useProgramLessons(pid);
  const columns = useMemo(() => getLessonColumns(pid), [pid]);
  const reorderLessons = useReorderLessons(pid);
  const { flashedLessonId } = useLessonSavedFlash();
  const tabPages = useProgramPageTabs(pid);

  return (
    <div className="tw-min-h-screen tw-bg-spa-bg tw-text-spa-on-bg">
      {inSpa && (
        <ProgramNavRail
          isLoadingTitle={isLoadingTitle}
          programColor={programColor}
          programId={pid}
          programTitle={programTitle}
        />
      )}
      <div className="tw-max-w-[1400px] tw-mx-auto tw-px-6 tw-py-4">
        <NodeCrumbs
          programId={pid}
          programTitle={programTitle}
          toLesson={id => lessonGuidesPath(pid, id, mount)}
          toProgram={id => programLessonsPath(id, mount)}
        />
        <SPAPageHeader actions={<AddLessonsMenu programId={pid} />} title="Table of Contents" />
        <div className="tw-mb-5 tw-border-0 tw-border-b tw-border-solid tw-border-spa-border">
          <NodeTabs label="Program pages" pages={tabPages} />
        </div>
        {isLoading && (
          <SPADataTable
            isLoading
            columns={columns}
            data={[]}
          />
        )}

        {!isLoading && hasUnits && (
          <>
            {units.map(unit => (
              <UnitLessonGroup
                key={unit.id}
                lessons={unit.lessons}
                programId={pid}
                unit={unit}
              />
            ))}
            {ghostLessons.length > 0 && (
              <div className="tw-mb-6">
                <div className={ghostHeaderClass}>
                  Unitless Lessons
                </div>
                <SPADataTable
                  columns={columns}
                  data={ghostLessons}
                  defaultSort={[]}
                  reorder={{ getRowId: lessonRowId, getRowLabel: lessonRowLabel, onReorder: reorderLessons }}
                  rowClassName={savedFlashRowClass(flashedLessonId)}
                />
              </div>
            )}
          </>
        )}

        {!isLoading && !hasUnits && (
          <SPADataTable
            columns={columns}
            data={allLessons}
            defaultSort={[]}
            emptyMessage="No lessons found for this program."
            reorder={{ getRowId: lessonRowId, getRowLabel: lessonRowLabel, onReorder: reorderLessons }}
            rowClassName={savedFlashRowClass(flashedLessonId)}
          />
        )}
      </div>
      {/* Floating copy-progress toast; renders only while a lesson/unit copy is in flight. */}
      <CopyProgressModal programId={pid} />
      <Outlet />
    </div>
  );
};

const ProgramLessonsPage = () => (
  <LessonSavedFlashProvider>
    <ProgramLessonsPageContent />
  </LessonSavedFlashProvider>
);

export default ProgramLessonsPage;
